import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useSpring, useTransform } from 'framer-motion';

interface NavLink {
  label: string;
  href: string;
}

interface ServiceLink {
  label: string;
  href: string;
  description: string;
  icon: string;
  gradient: string;
}

const navLinks: NavLink[] = [
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'About', href: '/about' },
];

const serviceLinks: ServiceLink[] = [
  {
    label: 'AI Audit Partnership',
    href: '/services/ai-audit-partnership',
    description: 'Ongoing audit + build partnership',
    icon: '🤝',
    gradient: 'from-blue-500 to-violet-500',
  },
  {
    label: 'AI Implementation',
    href: '/services/ai-implementation',
    description: 'Deploy production AI in weeks',
    icon: '⚡',
    gradient: 'from-violet-500 to-purple-500',
  },
  {
    label: 'VibeCode',
    href: '/services/vibecode',
    description: 'AI-powered rapid development',
    icon: '🚀',
    gradient: 'from-emerald-500 to-teal-500',
  },
];

// Scroll Progress Bar Component
const ScrollProgressBar: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500 via-violet-500 to-purple-500 origin-left z-[60]"
      style={{ scaleX }}
    />
  );
};

// Animated Service Card for Mega Menu
const ServiceCard: React.FC<{
  service: ServiceLink;
  index: number;
  onClose: () => void;
}> = ({ service, index, onClose }) => {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  }, []);

  return (
    <motion.a
      ref={cardRef}
      href={service.href}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.08, duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
      className="group relative flex flex-col p-4 sm:p-5 rounded-xl sm:rounded-2xl overflow-hidden"
      onClick={onClose}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setMousePosition({ x: 50, y: 50 });
      }}
    >
      {/* Animated gradient border */}
      <motion.div
        className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-r ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
        style={{ backgroundSize: '200% 200%' }}
        animate={isHovering ? { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] } : {}}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      />

      {/* Glass morphism background */}
      <div className="absolute inset-[1px] rounded-2xl bg-zinc-900/90 backdrop-blur-xl" />

      {/* Cursor-following highlight */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(300px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(59, 130, 246, 0.15), transparent 40%)`,
        }}
      />

      {/* Inner shadow */}
      <div
        className="absolute inset-[1px] rounded-2xl pointer-events-none"
        style={{
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), inset 0 -1px 1px rgba(0,0,0,0.3)',
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Icon with morphing animation */}
        <motion.div
          className="relative w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 rounded-lg sm:rounded-xl overflow-hidden"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        >
          {/* Icon glow */}
          <motion.div
            className={`absolute -inset-1 rounded-xl bg-gradient-to-r ${service.gradient} blur-md`}
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovering ? 0.6 : 0 }}
            transition={{ duration: 0.3 }}
          />
          <div className="relative w-full h-full rounded-xl bg-zinc-800 flex items-center justify-center border border-zinc-700/50">
            <motion.div
              className={`absolute inset-0 rounded-xl bg-gradient-to-br ${service.gradient} opacity-0`}
              animate={{ opacity: isHovering ? 0.2 : 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.span
              className="relative z-10 text-2xl"
              animate={isHovering ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] } : {}}
              transition={{ duration: 0.5 }}
            >
              {service.icon}
            </motion.span>
          </div>
        </motion.div>

        <h3 className="text-white font-semibold text-base sm:text-lg mb-0.5 sm:mb-1 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-zinc-300 transition-all duration-300">
          {service.label}
        </h3>
        <p className="text-zinc-500 text-xs sm:text-sm group-hover:text-zinc-400 transition-colors duration-300 line-clamp-2">
          {service.description}
        </p>

        {/* Arrow indicator */}
        <motion.div
          className="absolute bottom-4 right-4 w-8 h-8 rounded-lg bg-zinc-800/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          animate={isHovering ? { x: [0, 5, 0] } : {}}
          transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
        >
          <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </motion.div>
      </div>
    </motion.a>
  );
};

// Active page indicator with stretch animation
const NavLinkWithIndicator: React.FC<{
  link: NavLink;
  isActive: boolean;
}> = ({ link, isActive }) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <motion.a
      href={link.href}
      className="relative px-4 py-2 text-zinc-400 hover:text-white font-medium transition-colors duration-300 rounded-lg"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {link.label}

      {/* Hover indicator with stretch animation */}
      <motion.div
        className="absolute bottom-0 left-1/2 h-[2px] bg-gradient-to-r from-blue-500 to-violet-500 rounded-full"
        initial={{ width: 0, x: '-50%' }}
        animate={{
          width: isHovering || isActive ? '80%' : 0,
          x: '-50%',
        }}
        transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
      />

      {/* Active glow */}
      {isActive && (
        <motion.div
          className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/10 to-violet-500/10"
          layoutId="activeNavGlow"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        />
      )}
    </motion.a>
  );
};

// Notification Badge with pulse animation
const NotificationBadge: React.FC = () => {
  return (
    <motion.div
      className="absolute -top-1 -right-1 flex items-center justify-center"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
    >
      {/* Pulse ring */}
      <motion.div
        className="absolute w-4 h-4 rounded-full bg-emerald-500/50"
        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
      />
      {/* Badge */}
      <div className="relative w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-900" />
    </motion.div>
  );
};

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [activePath, setActivePath] = useState('');
  const headerRef = useRef<HTMLElement>(null);

  const { scrollY } = useScroll();

  // Logo morphing based on scroll
  const logoScale = useTransform(scrollY, [0, 100], [1, 0.9]);
  const logoOpacity = useTransform(scrollY, [0, 100], [1, 0.95]);

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 20);
  });

  useEffect(() => {
    setActivePath(window.location.pathname);
  }, []);

  // Close services dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    if (isServicesOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isServicesOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Scroll Progress Bar */}
      <ScrollProgressBar />

      <motion.header
        ref={headerRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-zinc-800/50 shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        {/* Subtle color tint overlay on scroll */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-violet-500/5 to-purple-500/5 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: isScrolled ? 1 : 0 }}
          transition={{ duration: 0.5 }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo with morphing animation */}
            <motion.a
              href="/"
              className="flex items-center gap-2 group relative"
              style={{ scale: logoScale, opacity: logoOpacity }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="relative w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center overflow-hidden"
                whileHover={{ rotate: 5 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                {/* Animated gradient background */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-blue-400 via-violet-500 to-purple-600"
                  animate={{
                    backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
                  }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                  style={{ backgroundSize: '200% 200%' }}
                />
                {/* Inner glow */}
                <motion.div
                  className="absolute inset-0 bg-white/20 rounded-lg"
                  animate={{ opacity: [0.2, 0.4, 0.2] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="relative z-10 text-white font-bold text-lg">A</span>
              </motion.div>

              {/* Text with morphing on scroll */}
              <motion.div className="flex items-baseline overflow-hidden">
                <motion.span
                  className="text-xl font-bold text-white tracking-tight"
                  animate={{ width: isScrolled ? 'auto' : 'auto' }}
                >
                  AI
                </motion.span>
                <motion.span
                  className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400 tracking-tight"
                  initial={{ width: 'auto', opacity: 1 }}
                  animate={{
                    width: isScrolled ? 0 : 'auto',
                    opacity: isScrolled ? 0 : 1,
                    marginLeft: isScrolled ? 0 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  Deploy
                </motion.span>
              </motion.div>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {/* Services Dropdown with Mega Menu */}
              <div className="relative">
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsServicesOpen(!isServicesOpen);
                  }}
                  className={`relative flex items-center gap-1.5 px-4 py-2 font-medium transition-colors duration-300 rounded-lg ${
                    isServicesOpen ? 'text-white bg-zinc-800/50' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Services
                  <motion.svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ rotate: isServicesOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>

                  {/* Active indicator */}
                  {activePath.includes('/services') && (
                    <motion.div
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-blue-500 to-violet-500 rounded-full"
                      layoutId="activeNavIndicator"
                    />
                  )}
                </motion.button>

                {/* Mega Menu Dropdown */}
                <AnimatePresence>
                  {isServicesOpen && (
                    <>
                      {/* Backdrop */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                        onClick={() => setIsServicesOpen(false)}
                      />

                      {/* Menu panel */}
                      <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
                        className="fixed sm:absolute top-20 sm:top-full left-1/2 -translate-x-1/2 mt-0 sm:mt-4 z-50 w-[calc(100vw-1.5rem)] sm:w-auto mx-auto"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Decorative gradient border */}
                        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-blue-500/30 via-violet-500/30 to-purple-500/30" />

                        {/* Glass morphism container */}
                        <div className="relative p-4 sm:p-6 rounded-2xl bg-zinc-900/95 backdrop-blur-2xl border border-zinc-800/50 shadow-2xl shadow-black/40">
                          {/* Inner glow */}
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 via-transparent to-violet-500/5 pointer-events-none" />

                          {/* Section label */}
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="flex items-center gap-2 mb-4 px-2"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                            <span className="text-zinc-500 text-xs font-semibold uppercase tracking-wider">
                              Our Services
                            </span>
                          </motion.div>

                          {/* Service cards grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 w-[calc(100vw-3rem)] sm:w-auto sm:min-w-[400px] lg:min-w-[800px] max-w-[calc(100vw-2rem)]">
                            {serviceLinks.map((service, index) => (
                              <ServiceCard
                                key={service.href}
                                service={service}
                                index={index}
                                onClose={() => setIsServicesOpen(false)}
                              />
                            ))}
                          </div>

                          {/* Bottom CTA */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mt-6 pt-4 border-t border-zinc-800/50"
                          >
                            <a
                              href="/contact"
                              className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-violet-500/10 border border-zinc-800/50 text-zinc-300 hover:text-white hover:border-zinc-700/50 transition-all duration-300 group"
                              onClick={() => setIsServicesOpen(false)}
                            >
                              <span className="text-sm font-medium">Not sure where to start?</span>
                              <span className="text-blue-400 group-hover:text-blue-300 transition-colors">
                                Book a free consultation
                              </span>
                              <motion.svg
                                className="w-4 h-4 text-blue-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                animate={{ x: [0, 3, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                              </motion.svg>
                            </a>
                          </motion.div>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              {/* Other nav links with active indicator */}
              {navLinks.map((link) => (
                <NavLinkWithIndicator
                  key={link.href}
                  link={link}
                  isActive={activePath === link.href}
                />
              ))}
            </nav>

            {/* Desktop CTA with notification badge */}
            <div className="hidden md:block">
              <motion.a
                href="/contact"
                className="group relative inline-flex items-center gap-2 px-5 py-2.5 bg-white text-zinc-900 font-semibold rounded-xl overflow-hidden transition-all duration-300"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Notification badge */}
                <NotificationBadge />

                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-blue-400 to-violet-400"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">
                  Book AI Audit
                </span>
                <motion.svg
                  className="w-4 h-4 relative z-10 group-hover:text-white transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </motion.svg>
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="md:hidden relative p-2.5 rounded-xl border border-zinc-800 text-white hover:bg-zinc-800/50 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              whileTap={{ scale: 0.95 }}
            >
              {/* Notification badge for mobile */}
              <NotificationBadge />

              <div className="w-5 h-5 flex flex-col items-center justify-center gap-1.5">
                <motion.span
                  className="w-5 h-0.5 bg-current rounded-full"
                  animate={{
                    rotate: isMenuOpen ? 45 : 0,
                    y: isMenuOpen ? 4 : 0,
                  }}
                  transition={{ duration: 0.2 }}
                />
                <motion.span
                  className="w-5 h-0.5 bg-current rounded-full"
                  animate={{
                    opacity: isMenuOpen ? 0 : 1,
                    scaleX: isMenuOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                />
                <motion.span
                  className="w-5 h-0.5 bg-current rounded-full"
                  animate={{
                    rotate: isMenuOpen ? -45 : 0,
                    y: isMenuOpen ? -4 : 0,
                  }}
                  transition={{ duration: 0.2 }}
                />
              </div>
            </motion.button>
          </div>

          {/* Mobile Menu with staggered reveal */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
                className="md:hidden overflow-hidden"
              >
                {/* Backdrop blur overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 top-20 bg-[#0a0a0a]/95 backdrop-blur-xl -z-10"
                />

                <nav className="flex flex-col gap-2 py-8 border-t border-zinc-800/50">
                  {/* Limited availability banner */}
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20"
                  >
                    <motion.div
                      className="w-2 h-2 rounded-full bg-emerald-500"
                      animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <span className="text-emerald-400 text-sm font-medium">
                      2 spots remaining for Q1
                    </span>
                  </motion.div>

                  {/* Services Section */}
                  <div className="px-2 mb-4">
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 }}
                      className="text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-3 px-2"
                    >
                      Services
                    </motion.p>
                    {serviceLinks.map((service, index) => (
                      <motion.a
                        key={service.href}
                        href={service.href}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.08, ease: [0.215, 0.61, 0.355, 1] }}
                        className="group flex items-start gap-4 px-3 py-4 rounded-xl hover:bg-zinc-800/50 transition-colors relative overflow-hidden"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {/* Hover gradient */}
                        <motion.div
                          className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-0 group-active:opacity-10 transition-opacity`}
                        />

                        {/* Icon */}
                        <motion.div
                          className="relative w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center border border-zinc-700/50 flex-shrink-0"
                          whileHover={{ scale: 1.05, rotate: 5 }}
                        >
                          <span className="text-xl">{service.icon}</span>
                        </motion.div>

                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-lg">{service.label}</p>
                          <p className="text-zinc-500 text-sm mt-0.5">{service.description}</p>
                        </div>

                        <svg
                          className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors mt-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.a>
                    ))}
                  </div>

                  {/* Divider */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent mx-4 my-2"
                  />

                  {/* Other Links */}
                  {navLinks.map((link, index) => (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.45 + index * 0.08, ease: [0.215, 0.61, 0.355, 1] }}
                      className="relative px-4 py-4 text-zinc-300 hover:text-white font-medium transition-colors rounded-xl hover:bg-zinc-800/50 mx-2 flex items-center justify-between"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                      <svg
                        className="w-5 h-5 text-zinc-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </motion.a>
                  ))}

                  {/* Mobile CTA */}
                  <motion.a
                    href="/contact"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
                    className="relative mx-2 mt-6 overflow-hidden rounded-xl"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {/* Animated gradient border */}
                    <motion.div
                      className="absolute -inset-[1px] bg-gradient-to-r from-blue-500 via-violet-500 to-purple-500 rounded-xl"
                      animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                      style={{ backgroundSize: '200% 200%' }}
                    />
                    <div className="relative flex items-center justify-center gap-3 px-5 py-4 bg-zinc-900 text-white font-semibold rounded-xl">
                      <span>Book Your AI Audit</span>
                      <motion.svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </motion.svg>
                    </div>
                  </motion.a>

                  {/* Trust indicators */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="flex flex-col items-center gap-3 mt-6 pt-6 border-t border-zinc-800/50"
                  >
                    <div className="flex items-center gap-2 text-zinc-500 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span>127+ AI systems deployed</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-500 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      <span>$12M+ in documented savings</span>
                    </div>
                  </motion.div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>
    </>
  );
};

export default Header;
